import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { Link } from 'react-router-dom';
import { IProductCatalogItem, backfillProducts, getCatalog, refreshCatalog } from '../../Services/productEntitlements';

const eur = (cents: number) => `${(cents / 100).toFixed(2).replace(/\.00$/, '')} €`;

const describeTiers = (item: IProductCatalogItem): string => {
	const p = item.price;
	if (!p) return '—';
	if (!p.tiers || p.tiers.length === 0) return `${eur(p.unitAmount)} / ${item.unit} / mois`;
	let from = 1;
	const parts = p.tiers.map((t) => {
		const range = t.upTo === 0 ? `${from}+` : `${from}–${t.upTo}`;
		from = t.upTo + 1;
		if (t.flatAmount > 0 && t.unitAmount === 0) return `${range} : forfait ${eur(t.flatAmount)}`;
		if (t.flatAmount === 0 && t.unitAmount === 0) return `${range} : inclus`;
		return `${range} : ${eur(t.unitAmount)} / unité`;
	});
	return `${p.tiersMode === 'graduated' ? 'progressif' : p.tiersMode === 'volume' ? 'VOLUME' : 'paliers'} · ${parts.join(' · ')}`;
};

/**
 * Le catalogue produit tel que Stripe le porte (pricing v2). Lecture seule :
 * les prix, l'inclus et les termes de dépassement se modifient DANS Stripe
 * (metadata `product`, `included_*`, `overage_*`) ; cet écran montre ce que
 * l'API sert au front, et permet de purger le cache (24 h) après une
 * modification quand le webhook `price.*`/`product.*` n'a pas fait le travail.
 *
 * ⚠️ Paliers : le mode doit être « graduated » (progressif). En « volume »,
 * Stripe facture toutes les unités au prix du palier atteint et le forfait du
 * 1er palier disparaît.
 */
export const ProductCatalog: React.FC = () => {
	const notify = useNotify();
	const [items, setItems] = useState<IProductCatalogItem[]>([]);
	const [loading, setLoading] = useState(false);

	const load = () =>
		getCatalog()
			.then((list) => setItems(list || []))
			.catch(() => setItems([]));

	useEffect(() => {
		load();
	}, []);

	const reload = async () => {
		setLoading(true);
		try {
			const list = await refreshCatalog();
			setItems(list || []);
			notify('Catalogue rechargé depuis Stripe', { type: 'success' });
		} catch (e: any) {
			notify(e?.message || 'Échec du rechargement', { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const volumeMode = items.filter((i) => i.price?.tiersMode === 'volume');

	return (
		<Box sx={{ p: 2 }}>
			<Card>
				<CardContent>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
						<Box>
							<Typography variant="h6">Produits (catalogue Stripe)</Typography>
							<Typography variant="caption" color="text.secondary">
								Lu sur les produits Stripe portant <code>metadata.product</code>. Prix et conditions se modifient dans Stripe, pas ici.
							</Typography>
						</Box>
						<Box sx={{ display: 'flex', gap: 1 }}>
							<Button
								variant="outlined"
								size="small"
								disabled={loading}
								onClick={async () => {
									if (!window.confirm('Recalculer les droits de TOUS les comptes ? Les comptes sous contrat (lignes admin) ne sont pas touchés ; les comptes Stripe repartent de leur abonnement.')) return;
									setLoading(true);
									try {
										const r = await backfillProducts(true);
										notify(`Droits recalculés : ${r.written} compte(s) écrit(s), ${r.skipped} ignoré(s), ${r.errors.length} erreur(s)`, { type: r.errors.length ? 'warning' : 'success' });
									} catch (e: any) {
										notify(e?.message || 'Échec du recalcul', { type: 'error' });
									} finally {
										setLoading(false);
									}
								}}
								title="Rejoue la photo initiale : Entreprise → usage réel en lignes contrat ; comptes Stripe → 0 puis remplis par le webhook"
							>
								Recalculer les droits
							</Button>
							<Button variant="contained" size="small" disabled={loading} onClick={reload}>
								Recharger depuis Stripe
							</Button>
						</Box>
					</Box>
					{volumeMode.length > 0 && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							{volumeMode.map((i) => i.name).join(', ')} : paliers en mode <strong>volume</strong> — toutes les unités sont facturées au prix du palier atteint et le
							forfait du 1ᵉʳ palier disparaît. Les passer en <strong>progressif</strong> (graduated) dans Stripe.
						</Alert>
					)}
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Produit</TableCell>
								<TableCell>Unité</TableCell>
								<TableCell>Inclus</TableCell>
								<TableCell>Tarif</TableCell>
								<TableCell>Dépassements</TableCell>
								<TableCell>Stripe</TableCell>
								<TableCell align="right" />
							</TableRow>
						</TableHead>
						<TableBody>
							{items.map((i) => (
								<TableRow key={i.product}>
									<TableCell>
										<strong>{i.name}</strong>
										<Typography variant="caption" display="block" color="text.secondary">
											{i.product}
										</Typography>
									</TableCell>
									<TableCell>{i.unit}</TableCell>
									<TableCell>{i.includedQty}</TableCell>
									<TableCell>
										<Typography variant="body2">{describeTiers(i)}</Typography>
									</TableCell>
									<TableCell>
										{i.terms && Object.keys(i.terms).length > 0
											? Object.entries(i.terms)
													.filter(([k]) => k !== 'included_qty')
													.map(([k, v]) => <Chip key={k} size="small" label={`${k}=${v}`} sx={{ mr: 0.5, mb: 0.5 }} />)
											: '—'}
									</TableCell>
									<TableCell>
										<Typography variant="caption" display="block">
											{i.stripeProductId}
										</Typography>
										<Typography variant="caption" display="block" color="text.secondary">
											{i.price?.stripePriceId ?? 'aucun prix'}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Button component={Link} to={`/products/${i.product}`} size="small">
											Détail
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</Box>
	);
};
